import Component from "../../Component";
import SentryService from "../services/SentryService";

export default abstract class BaseApi {
    protected readonly component: Component;
    protected readonly BASE_URL = "/api/booking";

    constructor(component: Component) {
        this.component = component;
    }

    protected async createHeaders(): Promise<Record<string, string>> {
        const token = await this.component.getAccessToken();
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        };
    }

    private async extractErrorMessage(response: Response): Promise<string> {
        let errorMessage = `Request failed with status ${response.status}: ${response.statusText}`;
        try {
            const errorBody = await response.text();
            if (errorBody) {
                // Try to parse as JSON first for structured error messages
                try {
                    const errorJson = JSON.parse(errorBody);
                    if (errorJson.message) {
                        errorMessage = errorJson.message;
                    } else if (errorJson.error) {
                        errorMessage = errorJson.error;
                    } else {
                        errorMessage = errorBody;
                    }
                } catch {
                    // If not JSON, use the raw text
                    errorMessage = errorBody;
                }
            }
        } catch {
            // If we can't read the response body, keep the default error message
        }
        return errorMessage;
    }

    protected async fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const method = options.method || "GET";
        const fullUrl = `${this.BASE_URL}${endpoint}`;

        return SentryService.instrumentApiCall(
            method,
            fullUrl,
            async () => {
                try {
                    const headers = await this.createHeaders();
                    const response = await fetch(fullUrl, {
                        ...options,
                        headers: {
                            ...headers,
                            ...options.headers
                        }
                    });

                    if (!response.ok) {
                        const errorMessage = await this.extractErrorMessage(response);
                        const error = new Error(errorMessage);
                        // Add response status to error for better Sentry tracking
                        (error as any).status = response.status;
                        (error as any).statusText = response.statusText;
                        throw error;
                    }

                    // INFO: The following logic handles cases where the response body might be empty. Cancelling Bookings returned an error before because there was no body.
                    // Some APIs return empty bodies for successful operations (like PUT/DELETE)
                    const contentType = response.headers.get("content-type");

                    // Try to get response text first to check if there's actual content
                    const responseText = await response.text();

                    if (!responseText) {
                        return undefined as T;
                    }

                    // If content type indicates JSON, parse it
                    if (contentType?.includes("application/json")) {
                        return JSON.parse(responseText) as T;
                    }

                    // For non-JSON responses with content, try parsing anyway
                    // This handles cases where APIs don't set proper content-type headers
                    try {
                        return JSON.parse(responseText) as T;
                    } catch {
                        return undefined as T;
                    }
                } catch (error) {
                    const errorMessage =
                        error instanceof Error ? error.message : "Unknown error occurred";
                    const enhancedError = new Error(`API Error: ${errorMessage}`);

                    // Preserve original error properties
                    if (error instanceof Error) {
                        (enhancedError as any).originalError = error;
                        (enhancedError as any).status = (error as any).status;
                        (enhancedError as any).statusText = (error as any).statusText;
                    }

                    throw enhancedError;
                }
            },
            options.body ? { requestBody: options.body } : undefined
        );
    }

    protected async postWithAuth<T>(endpoint: string, body: object): Promise<T> {
        return this.fetchWithAuth<T>(endpoint, {
            method: "POST",
            body: JSON.stringify(body)
        });
    }

    protected async putWithAuth<T>(endpoint: string, body: object): Promise<T> {
        return this.fetchWithAuth<T>(endpoint, {
            method: "PUT",
            body: JSON.stringify(body)
        });
    }

    protected async deleteWithAuth<T>(endpoint: string): Promise<T> {
        return this.fetchWithAuth<T>(endpoint, {
            method: "DELETE"
        });
    }

    protected async fetchWithAuthRAW<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        try {
            const headers = await this.createHeaders();
            const response = await fetch(`${this.BASE_URL}${endpoint}`, {
                ...options,
                headers: {
                    ...headers,
                    ...options.headers
                }
            });

            if (!response.ok) {
                const errorMessage = await this.extractErrorMessage(response);
                throw new Error(errorMessage);
            }
            // INFO: The following logic handles cases where the response body might be empty. Cancelling Bookings returned an error before because there was no body.
            // Some APIs return empty bodies for successful operations (like PUT/DELETE)

            // Try to get response text first to check if there's actual content
            const responseText = await response.text();
            return responseText as T;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            throw new Error(`Error: ${errorMessage}`);
        }
    }
}
